import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { notify } from '../utils/notify';
import { HiLockClosed, HiArrowLeft } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { resetPassword } = useAuthStore();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            notify.error(t('reset_password.passwords_do_not_match'));
            return;
        }

        if (password.length < 10) {
            notify.error(t('reset_password.password_too_short'));
            return;
        }

        setIsLoading(true);
        const result = await resetPassword(token, password);
        setIsLoading(false);

        if (result.success) {
            notify.success(t('reset_password.success'));
            navigate('/login');
        } else {
            notify.error(result.error);
        }
    };

    if (!token) {
        return (
            <div className="text-center space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-white">{t('reset_password.invalid_link')}</h2>
                    <p className="text-slate-400 mt-2">{t('reset_password.invalid_link_help')}</p>
                </div>
                <Link to="/forgot-password" className="btn-secondary inline-flex items-center gap-2">
                    <HiArrowLeft className="w-4 h-4" />
                    {t('reset_password.request_new_link')}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-white">{t('reset_password.title')}</h2>
                <p className="text-slate-400 mt-2">{t('reset_password.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('reset_password.new_password')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiLockClosed className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input pl-10 bg-slate-800 border-slate-700 text-white"
                            placeholder="••••••••"
                            minLength={10}
                            required
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('reset_password.confirm_password')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiLockClosed className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input pl-10 bg-slate-800 border-slate-700 text-white"
                            placeholder="••••••••"
                            minLength={10}
                            required
                        />
                    </div>
                </div>

                <button type="submit" disabled={isLoading} className="btn-primary w-full py-3">
                    {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            {t('reset_password.saving')}
                        </span>
                    ) : (
                        t('reset_password.submit')
                    )}
                </button>
            </form>

            <Link to="/login" className="flex items-center justify-center gap-2 text-slate-400 hover:text-white">
                <HiArrowLeft className="w-4 h-4" />
                {t('forgot_password.back_to_login')}
            </Link>
        </div>
    );
}
